import type { ActivatedRouteSnapshot, Route } from '@angular/router';

import { inject, isDevMode } from '@angular/core';
import { DELON_AUTH_CONFIG, DelonTokenService } from '@eliboo/micro-ng/auth-delon';
import { MICRO_ROUTE_HOST } from '@eliboo/micro-ng/router';
import { getAlainAuthConfig, getUserAuthGuardFn } from '@eliboo/shared/micro-ng';

import { bizAuthGuardFn, bizChildActivateGuardFn } from '../core';
import { payRouteInterceptorGuard, signatureGuard } from '../core/guard';
import {
  bizLicenseResolver,
  channelOperatorConfigResolver,
  invoiceResolver,
  LogoutResolver,
  NotificationPendResolver,
  NotificationPendTitleResolver,
  RenewalResolver,
} from '../core/resolves';
import { getIfTaxGuard, TaxRouteService } from '../core/route';
import { InvoiceService } from '../core/service/invoice.service';
import { TaxUserService } from '../core/service/user.service';
import { AnnualReportListService } from './annual-report-list/annual-report-list.service';
import { SmsVerificationService } from './sms-verification/sms-verification.service';
import { TaxRegistrationService } from './tax-registration/tax-registration.service';

export { getPathCommands, TaxRoutePath } from './tax-route-paths';

export const taxServiceRoutes: Route[] = [
  {
    path: 'eliboo-pay',
    canActivate: [payRouteInterceptorGuard],
    // 不需要组件，守卫内部会重定向，这里随便写或留空即可
    children: [
      {
        path: 'qr',
        loadComponent: () => import('./qr-code-pay/qr-code-pay.component').then((m) => m.QrCodePayComponent),
      },
    ],
  },
  {
    path: '',
    redirectTo: 'main/tax',
    pathMatch: 'full',
  },

  {
    path: 'main',
    canActivate: [
      getUserAuthGuardFn(TaxUserService, (injector, errMsg) => {
        return {
          commands: injector.get(TaxRouteService).getCommands('result'),
          navigationExtras: { queryParams: { msg: errMsg } },
        };
      }),
    ],
    canActivateChild: [bizChildActivateGuardFn(20)],
    children: [
      {
        path: 'tax',
        title: '主页',
        loadComponent: () => import('./tax/tax.component').then((m) => m.TaxComponent),
      },
      {
        path: 'invoice',
        title: '开票',
        providers: [InvoiceService],
        resolve: {
          channelConfig: invoiceResolver,
        },
        loadComponent: () => import('./invoice/invoice.component').then((m) => m.InvoiceComponent),
      },
      {
        path: 'invoice-form',
        title: '开票表单',
        loadComponent: () => import('./invoice-form/invoice-form.component').then((m) => m.InvoiceFormComponent),
      },
      {
        path: 'invoice-records',
        title: '开票记录',
        loadComponent: () => import('./invoice-records/invoice-records.component').then((m) => m.InvoiceRecordsComponent),
      },
      {
        path: 'tax-registration',
        title: '税务登记',
        providers: [TaxRegistrationService],
        loadComponent: () => import('./tax-registration/tax-registration.component').then((m) => m.TaxRegistrationComponent),
      },
      {
        path: 'tax-filing-history',
        title: '税务申报历史',
        canActivate: [getIfTaxGuard('未完成税务登记或未设置个税申报密码，不可查询税务申报历史')],
        loadComponent: () => import('./tax-filing-history/tax-filing-history.component').then((m) => m.TaxFilingHistoryComponent),
      },
      {
        path: 'tax-declare',
        title: '税务申报',
        canActivate: [getIfTaxGuard('未完成税务登记或未设置个税申报密码，不可进行个税申报，需优先完成个税申报密码提交待办')],
        loadComponent: () => import('./tax-declare/tax-declare.component').then((m) => m.TaxDeclareComponent),
      },
      {
        path: 'tax-payment-records',
        title: '税务缴费记录',
        loadComponent: () => import('./tax-payment-records/tax-payment-records.component').then((m) => m.TaxPaymentRecordsComponent),
      },
      {
        providers: [AnnualReportListService],
        path: '',
        children: [
          {
            path: 'annual-report-list',
            title: '工商年报',
            loadComponent: () => import('./annual-report-list/annual-report-list.component').then((m) => m.AnnualReportListComponent),
          },
          {
            path: 'annual-report-detail',
            title: '年报详情',
            loadComponent: () => import('./annual-report-detail/annual-report-detail.component').then((m) => m.AnnualReportDetailComponent),
          },
        ],
      },

      {
        path: 'tax-detail-list',
        title: '税费明细',
        loadComponent: () => import('./tax-detail-list/tax-detail-list.component').then((m) => m.TaxDetailListPageComponent),
      },
      {
        path: 'identity-verification',
        title: '身份认证',
        loadComponent: () => import('./identity-verification/identity-verification.component').then((m) => m.IdentityVerificationComponent),
      },
      {
        path: 'sms-verification',
        title: '接码待办',
        providers: [SmsVerificationService],
        loadComponent: () => import('./sms-verification/sms-verification.component').then((m) => m.SmsVerificationComponent),
      },
      {
        path: 'renewal',
        title: '营业执照服务续费',
        resolve: {
          renewalResolver: RenewalResolver,
        },
        loadComponent: () => import('./renewal/renewal.component').then((m) => m.RenewalComponent),
      },
      {
        path: 'logout',
        title: '营业执照注销',
        resolve: {
          logoutData: LogoutResolver,
        },
        loadComponent: () => import('./logout/logout.component').then((m) => m.LogoutComponent),
      },
      {
        path: 'notification-pending',
        title: NotificationPendTitleResolver,
        resolve: {
          notificationPending: NotificationPendResolver,
        },
        loadComponent: () => import('./notification-pending/notification-pending.component').then((m) => m.NotificationPendingComponent),
      },
      {
        path: 'business-change',
        title: '营业执照变更',
        loadComponent: () => import('./business-change/business-change.component').then((m) => m.BusinessChangeComponent),
      },
      {
        path: 'bank-card-info',
        title: '银行卡信息填写',
        loadComponent: () => import('./bank-card-info/bank-card-info.component').then((m) => m.BankCardInfoComponent),
      },
    ],
  },

  {
    path: 'sign',
    canActivate: [
      signatureGuard,
      getUserAuthGuardFn(
        TaxUserService,
        (injector, errMsg) => {
          return {
            commands: injector.get(TaxRouteService).getCommands('result'),
            navigationExtras: { queryParams: { msg: errMsg } },
          };
        },
        true,
      ),
    ],
    children: [
      {
        path: 'bank-card-info',
        title: '银行卡信息填写',
        loadComponent: () => import('./bank-card-info/bank-card-info.component').then((m) => m.BankCardInfoComponent),
      },
    ],
  },

  {
    path: 'biz',
    providers: [
      DelonTokenService,
      {
        provide: DELON_AUTH_CONFIG,
        useFactory: () => {
          return getAlainAuthConfig({ store_key: '_tax_biz_service_token', login_url: `${inject(MICRO_ROUTE_HOST)}/biz/login` });
        },
      },
    ],

    children: [
      {
        path: 'login',
        title: '登录',
        loadComponent: () => import('./biz_login/biz_login.component').then((m) => m.BizLoginComponent),
      },
      {
        path: '',
        canActivate: [
          bizAuthGuardFn((injector, errMsg) => {
            return {
              commands: injector.get(TaxRouteService).getCommands('result'),
              login_url: injector.get(TaxRouteService).getCommands('login'),
              navigationExtras: { queryParams: { msg: errMsg } },
            };
          }),
        ],

        children: [
          {
            path: 'license-upload',
            title: '营业执照上传',
            resolve: {
              bizLicense: bizLicenseResolver,
              channelConfig: channelOperatorConfigResolver,
            },
            loadComponent: () => import('./license-upload/license-upload.component').then((m) => m.LicenseUploadComponent),
          },
          {
            path: 'upload-result',
            title: '提交结果',
            resolve: {
              bizLicense: bizLicenseResolver,
            },
            loadComponent: () =>
              import('./license-upload/result/license-upload-result.component').then((m) => m.LicenseUploadResultComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'exception/:code',
    resolve: {
      msg: (route: ActivatedRouteSnapshot) => {
        const code = route.params['code'];
        switch (code) {
          case '404':
            return '页面不存在';
          case '500':
            return '服务器错误';
          default:
            return '未知错误';
        }
      },
    },
    loadComponent: () => import('./result/result.component').then((m) => m.ResultComponent),
  },
  {
    path: 'result',
    loadComponent: () => import('./result/result.component').then((m) => m.ResultComponent),
  },

  {
    path: 'logout-sign',
    title: '营业执照注销',
    resolve: {
      logoutData: LogoutResolver,
    },
    loadComponent: () => import('./logout/logout.component').then((m) => m.LogoutComponent),
  },

  {
    path: 'tutorial',
    loadComponent: () => import('./tutorial/tutorial.component').then((m) => m.TutorialComponent),
  },
  { path: '**', redirectTo: 'exception/404', pathMatch: 'full' },
];

if (isDevMode()) {
  taxServiceRoutes.push({
    path: 'demo',
    loadChildren: () => import('./demos/lib-demos.routes').then((routes) => routes.taxDemosRoutes),
  });
}
